import React, { memo } from 'react';
import { useViewport } from '../../../contexts/ViewportContext';
import { useStatusBarInfo } from '../../../hooks/useStatusBarInfo';
import { logger } from '../../../utils/logger';

// Memoize the mobile skeleton component to prevent re-rendering when parent components re-render
const MobileDashboardSkeleton: React.FC = memo(
  () => {
    // Get viewport state
    const { isMobile } = useViewport();

    // Get status bar info for mobile adjustments
    const { isNativeApp, statusBarHeight, navigationBarHeight } = useStatusBarInfo();

    // Log status bar info for debugging
    React.useEffect(() => {
      if (isNativeApp && isMobile) {
        logger.capacitor('MobileDashboardSkeleton: Using status bar height', {
          statusBarHeight,
          navigationBarHeight,
        });
      }
    }, [isNativeApp, statusBarHeight, navigationBarHeight, isMobile]);

    // Calculate header height with status bar
    const headerHeight = isNativeApp && isMobile ? `calc(3.5rem + ${statusBarHeight}px)` : '3.5rem';

    // Calculate content top padding with status bar
    const contentTopPadding =
      isNativeApp && isMobile ? `calc(3.5rem + ${statusBarHeight}px)` : '3.5rem';

    return (
      <div className="h-full overflow-hidden bg-[var(--content-bg-color)] flex flex-col">
        {/* Top fixed header area - with status bar padding */}
        <div
          className="fixed top-0 left-0 right-0 z-[40] bg-[var(--content-bg-color)]"
          style={{
            paddingTop: isNativeApp && isMobile ? `${statusBarHeight}px` : '0',
            height: headerHeight,
          }}
        >
          {/* Menu button */}
          <div
            className="absolute left-4 z-[41]"
            style={{
              top: isNativeApp && isMobile ? `calc(50% + ${statusBarHeight / 2}px)` : '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <div className="w-8 h-8 bg-gray-200 rounded-md relative overflow-hidden">
              <div className="animate-shimmer absolute inset-0"></div>
            </div>
          </div>

          {/* Column switcher - center (all neutral gray) */}
          <div
            className="absolute left-0 right-0 flex items-center justify-center h-14 z-[40] pointer-events-none"
            style={{
              top: isNativeApp && isMobile ? `${statusBarHeight}px` : 0,
              height: '3.5rem', // 14 in tailwind = 3.5rem
            }}
          >
            <div className="flex items-center space-x-2">
              {['overdue', 'today', 'thisWeek', 'highlighted'].map((section, index) => (
                <div
                  key={section}
                  className={`h-8 flex items-center px-3 rounded-md bg-gray-200 relative overflow-hidden ${
                    index === 0 ? 'w-20' : 'w-16'
                  }`}
                >
                  <div className="animate-shimmer absolute inset-0"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Filter button */}
          <div
            className="absolute right-4 z-[41]"
            style={{
              top: isNativeApp && isMobile ? `calc(50% + ${statusBarHeight / 2}px)` : '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <div className="w-8 h-8 bg-gray-200 rounded-md relative overflow-hidden">
              <div className="animate-shimmer absolute inset-0"></div>
            </div>
          </div>
        </div>

        {/* Task column (only one visible at a time in mobile) - adjust for status bar and navigation bar */}
        <div
          className="flex-1 overflow-y-auto px-4 w-full fixed inset-0 bg-[var(--content-bg-color)]"
          style={{
            paddingTop: contentTopPadding,
            paddingBottom:
              isNativeApp && isMobile ? `calc(1rem + ${navigationBarHeight}px)` : '1rem',
          }}
        >
          <div className="w-full rounded-lg mb-4">
            {/* Mobile uses centered title without icon - adding more top margin to match real layout */}
            <div className="flex justify-center items-center mt-8 mb-3">
              <div className="h-6 bg-gray-200 rounded-md w-28 relative overflow-hidden">
                <div className="animate-shimmer absolute inset-0"></div>
              </div>
            </div>

            {/* Container for skeleton task cards */}
            <div className="space-y-3">
              {/* Generate 5-7 skeleton task cards, randomly sized */}
              {[...Array(Math.floor(Math.random() * 3) + 5)].map((_, cardIndex) => {
                // Randomize content variations for more realistic appearance
                const hasTags = Math.random() > 0.5;
                const hasDesc = Math.random() > 0.3;

                return (
                  <div
                    key={cardIndex}
                    className="p-3 pt-2 bg-white border-l-[5px] border-red-300 rounded-lg mb-3 shadow-sm"
                  >
                    {/* Project/topic info */}
                    <div className="h-3 bg-gray-200 rounded w-1/2 mb-1 relative overflow-hidden max-w-[calc(100%-60px)]">
                      <div className="animate-shimmer absolute inset-0"></div>
                    </div>

                    {/* Task title */}
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-1.5 relative overflow-hidden pr-6 font-montserrat">
                      <div className="animate-shimmer absolute inset-0"></div>
                    </div>

                    {/* Task description (sometimes) */}
                    {hasDesc && (
                      <div className="space-y-1 mb-3">
                        <div className="h-3 bg-gray-200 rounded w-full relative overflow-hidden">
                          <div className="animate-shimmer absolute inset-0"></div>
                        </div>
                        <div className="h-3 bg-gray-200 rounded w-5/6 relative overflow-hidden">
                          <div className="animate-shimmer absolute inset-0"></div>
                        </div>
                      </div>
                    )}

                    {/* Deadline indicator */}
                    <div className="h-4 bg-gray-200 rounded w-28 mb-2 flex items-center relative overflow-hidden">
                      <div className="h-3 w-3 bg-gray-300 rounded-full mr-2 relative overflow-hidden">
                        <div className="animate-shimmer absolute inset-0"></div>
                      </div>
                      <div className="animate-shimmer absolute inset-0"></div>
                    </div>

                    {/* Tags (sometimes) */}
                    {hasTags && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        <div className="h-4 bg-gray-200 rounded-full w-12 relative overflow-hidden">
                          <div className="animate-shimmer absolute inset-0"></div>
                        </div>
                        <div className="h-4 bg-gray-200 rounded-full w-16 relative overflow-hidden">
                          <div className="animate-shimmer absolute inset-0"></div>
                        </div>
                        {Math.random() > 0.5 && (
                          <div className="h-4 bg-gray-200 rounded-full w-14 relative overflow-hidden">
                            <div className="animate-shimmer absolute inset-0"></div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  },
  () => true
); // Always return true from memo comparison to prevent re-rendering

export default MobileDashboardSkeleton;
